import React, { useState, useEffect } from 'react';
import './App.css';
import Login from './components/Login';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import Vehicles from './components/Vehicles';
import Repairs from './components/Repairs';
import Approvals from './components/Approvals';
import MRReport from './components/MRReport';
import InvoiceScanner from './components/InvoiceScanner';
import ExportQuotes from './components/ExportQuotes';
import UserManagement from './components/UserManagement';
import Servicing from './components/Servicing';
import UserTelegramSettings from './components/UserTelegramSettings';
import FleetData from './components/FleetData';
import FireExtinguishers from './components/FireExtinguishers';
import ChangePassword from './components/ChangePassword';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showTelegramSettings, setShowTelegramSettings] = useState(false);
  const [resetPasswordToken, setResetPasswordToken] = useState(null);

  // Check for reset password token in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');
    const path = window.location.pathname;
    
    if (path === '/reset-password' && resetToken) {
      setResetPasswordToken(resetToken);
    }
  }, []);

  const handleBackToLogin = () => {
    setResetPasswordToken(null);
    // Clear URL params
    window.history.replaceState({}, document.title, '/');
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser && token) {
      const parsed = JSON.parse(storedUser);
      // Sessions from before the is_super_admin flag must log in again once
      if (!('is_super_admin' in parsed)) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setToken(null);
        return;
      }
      setUser(parsed);
      // Refresh the saved copy from the server, so a changed role (or a new
      // "choose your password" / Telegram requirement) shows without logging out
      fetch(`${BACKEND_URL}/api/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => {
          if (r.status === 401) { handleLogout(); return null; }
          return r.ok ? r.json() : null;
        })
        .then(fresh => {
          if (!fresh) return;
          const merged = { ...parsed, ...fresh };
          setUser(merged);
          localStorage.setItem('user', JSON.stringify(merged));
        })
        .catch(() => {});
    }
  }, [token]);

  const updateUser = (fresh) => {
    const merged = { ...user, ...fresh };
    setUser(merged);
    localStorage.setItem('user', JSON.stringify(merged));
  };

  const handleLogin = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', authToken);
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setCurrentPage('dashboard');
    setMobileMenuOpen(false);
  };

  const navigateTo = (page) => {
    setCurrentPage(page);
    setMobileMenuOpen(false);
  };

  // Show reset password page if token is present in URL
  if (resetPasswordToken) {
    return <ResetPassword token={resetPasswordToken} onBackToLogin={handleBackToLogin} />;
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  if (user.must_change_password) {
    return <ChangePassword token={token} user={user} onDone={updateUser} onLogout={handleLogout} />;
  }

  return (
    <div className="app">
      {/* Mobile Header */}
      <header className="mobile-header">
        <button 
          className="hamburger-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>
        <h1 className="mobile-title">FLEET CONTROL</h1>
        <span className="mobile-page-indicator">
          {currentPage === 'dashboard' && '📊'}
          {currentPage === 'vehicles' && '🚛'}
          {currentPage === 'repairs' && '🔧'}
          {currentPage === 'approvals' && '✓'}
          {currentPage === 'mr-report' && '📄'}
          {currentPage === 'invoice-scanner' && '📸'}
          {currentPage === 'export-quotes' && '📥'}
          {currentPage === 'users' && '👥'}
          {currentPage === 'fire-extinguishers' && '🧯'}
        </span>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${mobileMenuOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <h1 data-testid="app-title">FLEET CONTROL</h1>
          <p className="user-info" data-testid="user-info">{user.name} • {user.role.toUpperCase()}</p>
        </div>
        <nav className="sidebar-nav">
          <button 
            data-testid="nav-dashboard"
            className={currentPage === 'dashboard' ? 'active' : ''} 
            onClick={() => navigateTo('dashboard')}
          >
            <span className="nav-icon">📊</span> Dashboard
          </button>
          <button 
            data-testid="nav-vehicles"
            className={currentPage === 'vehicles' ? 'active' : ''} 
            onClick={() => navigateTo('vehicles')}
          >
            <span className="nav-icon">🚛</span> Vehicles
          </button>
          <button 
            data-testid="nav-repairs"
            className={currentPage === 'repairs' ? 'active' : ''} 
            onClick={() => navigateTo('repairs')}
          >
            <span className="nav-icon">🔧</span> Repairs
          </button>
          <button 
            data-testid="nav-approvals"
            className={currentPage === 'approvals' ? 'active' : ''} 
            onClick={() => navigateTo('approvals')}
          >
            <span className="nav-icon">✓</span> Approvals
          </button>
          <button 
            data-testid="nav-servicing"
            className={currentPage === 'servicing' ? 'active' : ''} 
            onClick={() => navigateTo('servicing')}
          >
            <span className="nav-icon">🛠️</span> Servicing
          </button>
          <button
            data-testid="nav-fire-extinguishers"
            className={currentPage === 'fire-extinguishers' ? 'active' : ''}
            onClick={() => navigateTo('fire-extinguishers')}
          >
            <span className="nav-icon">🧯</span> Fire Extinguishers
          </button>
          <button 
            data-testid="nav-mr-report"
            className={currentPage === 'mr-report' ? 'active' : ''} 
            onClick={() => navigateTo('mr-report')}
          >
            <span className="nav-icon">📄</span> M&R Reports
          </button>
          <button 
            data-testid="nav-invoice-scanner"
            className={currentPage === 'invoice-scanner' ? 'active' : ''} 
            onClick={() => navigateTo('invoice-scanner')}
          >
            <span className="nav-icon">📸</span> Invoice Scanner
          </button>
          <button 
            data-testid="nav-export-quotes"
            className={currentPage === 'export-quotes' ? 'active' : ''} 
            onClick={() => navigateTo('export-quotes')}
          >
            <span className="nav-icon">📥</span> Export / Navision
          </button>
          <button 
            data-testid="nav-fleet-data"
            className={currentPage === 'fleet-data' ? 'active' : ''} 
            onClick={() => navigateTo('fleet-data')}
          >
            <span className="nav-icon">📊</span> Fleet Data
          </button>
          {user?.is_super_admin && (
            <button 
              data-testid="nav-users"
              className={currentPage === 'users' ? 'active' : ''} 
              onClick={() => navigateTo('users')}
            >
              <span className="nav-icon">👥</span> Users
            </button>
          )}
          <button 
            data-testid="nav-telegram"
            onClick={() => setShowTelegramSettings(true)}
            style={{ background: '#0088cc', color: 'white' }}
          >
            <span className="nav-icon">📱</span> My Telegram
          </button>
          <button data-testid="logout-btn" className="logout-btn" onClick={handleLogout}>
            <span className="nav-icon">🚪</span> Logout
          </button>
        </nav>
      </aside>

      {/* User Telegram Settings Modal */}
      {showTelegramSettings && (
        <UserTelegramSettings 
          token={token} 
          user={user} 
          onClose={() => {
            setShowTelegramSettings(false);
            fetch(`${BACKEND_URL}/api/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } })
              .then(r => (r.ok ? r.json() : null)).then(fresh => fresh && updateUser(fresh)).catch(() => {});
          }}
        />
      )}

      {/* Main Content */}
      <main className="main-content">
        {user.telegram_2fa_setup_needed && (
          <div className="alert alert-warning" data-testid="tfa-setup-banner" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <span style={{ flex: 1, minWidth: 220 }}>
              <strong>Please link your Telegram.</strong> Two-step login is on for your role: once linked, you'll get a
              login code there each time you log in.
            </span>
            <button className="btn btn-sm btn-primary" onClick={() => setShowTelegramSettings(true)}>Link Telegram</button>
          </div>
        )}
        {currentPage === 'dashboard' && <Dashboard token={token} user={user} />}
        {currentPage === 'vehicles' && <Vehicles token={token} />}
        {currentPage === 'repairs' && <Repairs token={token} user={user} />}
        {currentPage === 'approvals' && <Approvals token={token} user={user} />}
        {currentPage === 'servicing' && <Servicing token={token} user={user} />}
        {currentPage === 'fire-extinguishers' && <FireExtinguishers token={token} user={user} />}
        {currentPage === 'mr-report' && <MRReport token={token} />}
        {currentPage === 'invoice-scanner' && <InvoiceScanner token={token} />}
        {currentPage === 'export-quotes' && <ExportQuotes token={token} />}
        {currentPage === 'fleet-data' && <FleetData token={token} user={user} />}
        {currentPage === 'users' && <UserManagement token={token} user={user} />}
      </main>
    </div>
  );
}

export default App;
